/**
 * THE ONE PRINTER, AS THE WHOLE BACK OFFICE SEES IT.
 *
 * A USB printer is a single physical thing on this computer; the chip in the top bar, the printer
 * panel and an order's print sheet are three windows onto it. They share this store, so the chip
 * cannot say "Ready" while the sheet says "Lid open".
 *
 * Truth is re-read, not remembered: the status is polled while the tab is visible and nothing is
 * printing, because between two prints a person opens the lid, reloads paper, pulls the cable, or
 * switches the printer off — none of which the browser is told about except the cable.
 */

import { useSyncExternalStore } from 'react';
import { masungPrinter, type MasungTicketData } from '../masung-printer/printer';
import { deviceKey, loadProfile, saveProfile as persistProfile, PROFILE_EVENT, type PrinterProfile } from '../masung-printer/profile';
import { installSimIfEnabled } from '../masung-printer/simulator';
import { describe, type PrinterFacts, type PrinterSituation } from './printerState';
import { DEFAULT_TEMPLATE, programmeLedger } from './templates';

export type TicketResult = 'pending' | 'printing' | 'printed' | 'uncertain' | 'not-sent';

export interface PrintJob {
  label: string;
  results: TicketResult[];
  /** Why it stopped early, in plain words. */
  stopReason?: string;
  finishedAt?: number;
}

interface State extends Omit<PrinterFacts, 'printing'> {
  deviceName?: string;
  statusAt?: number;
  job: PrintJob | null;
  printingIndex: number | null;
}

const KNOWN_KEY = 'ticket-printer-known';
let state: State = {
  supported: typeof navigator !== 'undefined' && 'usb' in navigator,
  secure: typeof window !== 'undefined' ? window.isSecureContext : true,
  connected: false, connecting: false, restarting: false,
  knownPrinter: typeof localStorage !== 'undefined' && localStorage.getItem(KNOWN_KEY) === '1',
  connectError: null, status: null, profile: null, job: null, printingIndex: null,
};
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };

const profileOfConnected = (): PrinterProfile | null => {
  const id = masungPrinter.identity();
  return id ? loadProfile(deviceKey(id)) : null;
};

let started = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function start() {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (installSimIfEnabled(masungPrinter)) set({ supported: true, secure: true });
  (window as any).__tktPrinter = masungPrinter;

  masungPrinter.onStatusChange((s) => {
    if (s.connected) {
      const profile = profileOfConnected();
      masungPrinter.applyProfile(profile);
      localStorage.setItem(KNOWN_KEY, '1');
      set({ connected: true, connecting: false, restarting: false, connectError: null, knownPrinter: true, deviceName: s.deviceName, profile });
      void refresh();
    } else {
      const restarting = /restarting/i.test(s.error ?? '');
      set({ connected: false, restarting, status: null, connectError: restarting || /disconnected/i.test(s.error ?? '') ? null : s.error ?? null });
    }
  });
  window.addEventListener(PROFILE_EVENT, () => { if (state.connected) { const profile = profileOfConnected(); masungPrinter.applyProfile(profile); set({ profile }); } });

  // Switched back on / replugged: WebUSB keeps the permission, so take it back without a click.
  const usb = (navigator as any).usb;
  usb?.addEventListener?.('connect', () => { if (!state.connected) void autoConnect(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void refresh(); });

  pollTimer = setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 4000);
  void autoConnect();
}

async function autoConnect() {
  if (state.connected || state.connecting) return;
  try {
    set({ connecting: true });
    const ok = await masungPrinter.autoConnect('80');
    if (!ok) { set({ connecting: false }); return; }
    // Already open (another part of the app got there first): no status event will arrive, so
    // take the state from the device itself instead of waiting on "Connecting…" for ever.
    if (masungPrinter.connected && !state.connected) {
      const profile = profileOfConnected();
      masungPrinter.applyProfile(profile);
      set({ connected: true, connecting: false, knownPrinter: true, deviceName: masungPrinter.device?.productName || 'USB printer', profile });
      void refresh();
    }
  } catch (e: any) {
    set({ connecting: false, connectError: e?.message ?? String(e) });
  }
}

/** Re-read the printer's own status. Skipped mid-job: the job checks between tickets itself. */
export async function refresh() {
  if (!state.connected || state.printingIndex !== null || state.restarting) return;
  try {
    const status = await masungPrinter.readStatusRaw();
    set({ status, statusAt: Date.now() });
  } catch {
    /* the disconnect listener reports a lost device */
  }
}

/** Opens the browser's USB chooser — must run from a click. */
export async function connect(): Promise<string | null> {
  const { connectWithDiagnosis } = await import('./connectFailure');
  set({ connecting: true, connectError: null });
  const failure = await connectWithDiagnosis(() => masungPrinter.connect('80'));
  set({ connecting: false, connectError: failure });
  return failure;
}

export async function disconnect() {
  await masungPrinter.disconnect();
  set({ connected: false, status: null });
}

export function saveProfile(p: PrinterProfile): PrinterProfile {
  const saved = persistProfile(p);
  masungPrinter.applyProfile(saved);
  set({ profile: saved });
  return saved;
}

/** Layout settings live on the printer's profile: they describe the printer + its stock, not the till. */
export function updateLayout(patch: Partial<Pick<PrinterProfile, 'showHeader' | 'template' | 'marginDots' | 'topDots' | 'paperLeftDots' | 'paperWidthDots'>>) {
  const p = state.profile;
  if (!p) return;
  saveProfile({ ...p, ...patch });
  // The next run leads with one throwaway slip — see MasungPrinter.needsAlignment.
}

export const situation = (): PrinterSituation =>
  describe({ ...state, printing: state.printingIndex !== null && state.job ? { index: state.printingIndex + 1, total: state.job.results.length } : null });

/**
 * PRINT, TICKET BY TICKET, AND KNOW WHAT CAME OUT.
 *
 * - Nothing is sent into an error: the printer would hold it and print it later by surprise.
 * - After each ticket the printer is asked again. If it stopped, the ticket in flight is UNCERTAIN —
 *   it may still come out once the error is fixed — and the rest are NOT SENT. That is what lets the
 *   sheet say "print the remaining 2" without duplicating the one that may yet appear.
 */
export async function printTickets(tickets: MasungTicketData[], label: string): Promise<PrintJob> {
  await refresh();
  const sit = situation();
  const job: PrintJob = { label, results: tickets.map(() => 'pending') };
  if (sit.blocksPrinting && state.connected === false) {
    job.stopReason = sit.headline; job.results = job.results.map(() => 'not-sent'); job.finishedAt = Date.now();
    set({ job }); return job;
  }
  if (sit.blocksPrinting) {
    job.stopReason = `${sit.headline}. ${sit.todo ?? ''}`.trim(); job.results = job.results.map(() => 'not-sent'); job.finishedAt = Date.now();
    set({ job }); return job;
  }
  const p = state.profile;
  const mode = p?.paperType === 'continuous' ? 'cut' : 'tear';
  const showHeader = p?.showHeader ?? true;
  const template = p?.template ?? DEFAULT_TEMPLATE;
  set({ job, printingIndex: 0 });
  masungPrinter.beginPrintJob();
  if (masungPrinter.needsAlignment) {
    // A layout setting changed: burn one marked scrap, not a customer's ticket.
    try { await masungPrinter.printAlignmentTicket(mode); } catch { /* reported by the ticket that follows */ }
  }
  const results = [...job.results];
  const update = (i: number | null, extra: Partial<PrintJob> = {}) => set({ printingIndex: i, job: { ...job, ...extra, results: [...results] } });
  let stopReason: string | undefined;
  for (let i = 0; i < tickets.length; i++) {
    results[i] = 'printing'; update(i);
    try {
      if (template === 'classic') await masungPrinter.printTicket({ ...tickets[i], mode, showHeader });
      else await masungPrinter.printLayout(programmeLedger(tickets[i], masungPrinter.layoutWidthDots, p), mode, showHeader, { accents: p?.typeCaps?.accents !== false });
    } catch (e: any) {
      results[i] = 'uncertain'; stopReason = e?.message ?? String(e); break;
    }
    // Give the last one time to actually run out of the printer before judging it.
    if (i === tickets.length - 1) await new Promise((r) => setTimeout(r, 1500));
    const st = await masungPrinter.readStatusRaw().catch(() => null);
    set({ status: st, statusAt: Date.now() });
    if (st && st[0] !== null && st[0] & 0x08) {
      results[i] = 'uncertain';
      const s = describe({ ...state, status: st, printing: null });
      stopReason = `${s.headline}. ${s.todo ?? ''}`.trim();
      break;
    }
    results[i] = 'printed';
  }
  for (let i = 0; i < results.length; i++) if (results[i] === 'pending') results[i] = 'not-sent';
  const done: PrintJob = { ...job, results, stopReason, finishedAt: Date.now() };
  set({ job: done, printingIndex: null });
  return done;
}

export function clearJob() { set({ job: null }); }

export function usePrinter() {
  start();
  const s = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state, () => state);
  return { ...s, situation: situation(), printer: masungPrinter };
}

export { masungPrinter };
export const stopPolling = () => { if (pollTimer) clearInterval(pollTimer); };

